import { object, string, number, array, boolean } from "yup";
import { TFunction } from "i18n";
import { Message } from "yup/lib/types";
import { IGoogleLocation } from "shared-services";
import {
  NON_BINARY_FACET_ID,
  CUSTOM_FACET_ID,
} from "../../../../hooks/useAdditionalGenderOptions";
import { IAboutYouFormPhotoModel } from "../../../../formModels/AboutYouFormModel";
import { IGraduatesApplicationQuery } from "../types";
import {
  DetailsPageFormFields,
  DetailsPageHeadshotFormFields,
  IGraduatesDetailsFormFields,
} from "./constants";

export const INPUT_FIELDS_MAX_LENGTH = 75;

export const headshotFormInitialValues: IAboutYouFormPhotoModel = {
  [DetailsPageHeadshotFormFields.Photo]: [],
  [DetailsPageHeadshotFormFields.PhotographerName]: "",
};

export const formInitialValues = {
  [DetailsPageFormFields.Agent]: null,
  [DetailsPageFormFields.Gender]: null,
  [DetailsPageFormFields.GenderNonBinaryCustom]: null,
  [DetailsPageFormFields.GenderFurtherInfo]: "",
  [DetailsPageFormFields.DisplayGender]: false,
  [DetailsPageFormFields.Location]: [],
};

export function getInitialValues(
  graduatesApp?: IGraduatesApplicationQuery,
  locationsData: IGoogleLocation[] = []
): IGraduatesDetailsFormFields {
  if (!graduatesApp)
    return { ...formInitialValues, ...headshotFormInitialValues };

  return {
    [DetailsPageHeadshotFormFields.Photo]:
      graduatesApp?.photograph?.photoKey || [],
    [DetailsPageHeadshotFormFields.PhotographerName]:
      graduatesApp?.photograph?.photographerName ||
      headshotFormInitialValues.photographerName,
    [DetailsPageFormFields.Agent]: graduatesApp.agencyRefNumber || null,
    [DetailsPageFormFields.Gender]: graduatesApp.gender,
    [DetailsPageFormFields.GenderNonBinaryCustom]:
      graduatesApp?.customOrNonBinaryGender?.lookupCode || null,
    [DetailsPageFormFields.GenderFurtherInfo]: graduatesApp.genderInfo || "",
    [DetailsPageFormFields.DisplayGender]: graduatesApp.isVisibleGender,
    [DetailsPageFormFields.Location]:
      locationsData?.map((location) => {
        return {
          id: location.googlePlaceId,
          name: location.value,
        };
      }) || [],
  };
}

export function getDetailsPageValidation(t: TFunction) {
  return object().shape({
    [DetailsPageFormFields.Agent]: number().nullable().optional(),
    [DetailsPageFormFields.Gender]: number().required(
      t("common:validation.error.entryRequired", {
        fieldName: t("common:label.gender"),
      }) as Message<{}>
    ),
    [DetailsPageFormFields.GenderNonBinaryCustom]: number()
      .nullable()
      .when(DetailsPageFormFields.Gender, {
        is: (value: number) =>
          value === NON_BINARY_FACET_ID || value === CUSTOM_FACET_ID,
        then: (schema) =>
          schema.required(
            t("common:validation.error.genderNonBinaryCustom") as Message<{}>
          ),
      }),
    [DetailsPageFormFields.GenderFurtherInfo]: string()
      .optional()
      .max(INPUT_FIELDS_MAX_LENGTH),
    [DetailsPageFormFields.DisplayGender]: boolean(),
    [DetailsPageFormFields.Location]: array()
      .of(
        object().shape({
          id: string(),
          name: string(),
        })
      )
      .required(
        t("common:validation.error.entryRequired", {
          fieldName: "Location",
        }) as Message<{}>
      )
      .min(1, t("common:performer.profile.location.listValidation")),
  });
}
